import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardShell from '@/components/DashboardShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  User, Star, Calendar, MapPin, Award, Shield, Briefcase,
  Edit2, Camera, Check, X, Loader2, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Review Tags Display
const ReviewTags = ({ tags }) => {
  if (!tags || tags.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {tags.map((tag, i) => (
        <span
          key={i}
          className="px-2 py-0.5 bg-zinc-100 text-zinc-600 rounded-full text-xs font-medium"
        >
          {tag}
        </span>
      ))}
    </div>
  );
};

// Single Review Card
const ReviewCard = ({ review }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  };

  return (
    <div className="py-4 border-b border-zinc-100 last:border-0">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-zinc-500" />
          </div>
          <div>
            <p className="font-medium text-sm text-zinc-900">{review.reviewer_name || 'User'}</p>
            <p className="text-xs text-zinc-500">{review.job_category} • {formatDate(review.job_date || review.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`w-4 h-4 ${i < review.rating ? 'text-zinc-900 fill-zinc-900' : 'text-zinc-300'}`}
            />
          ))}
        </div>
      </div>
      {review.comment && (
        <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{review.comment}</p>
      )}
      <ReviewTags tags={review.tags} />
    </div>
  );
};

// Star Rating Input
const StarRatingInput = ({ value, onChange, disabled }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="p-1 transition-transform hover:scale-110 disabled:cursor-not-allowed"
        >
          <Star
            className={`w-8 h-8 transition-colors ${
              star <= (hover || value)
                ? 'text-zinc-900 fill-zinc-900'
                : 'text-zinc-300 hover:text-zinc-400'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Trust Badges
const TrustBadge = ({ icon: Icon, label, verified }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
    verified ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-500'
  }`}>
    <Icon className="w-4 h-4" />
    <span className="text-sm font-medium">{label}</span>
    {verified && <Check className="w-4 h-4" />}
  </div>
);

const ProfilePage = () => {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Edit form state
  const [displayName, setDisplayName] = useState('');
  const [about, setAbout] = useState('');
  
  // Is viewing own profile
  const isOwnProfile = !userId || userId === currentUser?.id;

  useEffect(() => {
    fetchProfile();
  }, [userId, currentUser]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const endpoint = isOwnProfile ? '/profile/me' : `/profiles/${userId}`;
      const response = await axios.get(`${API_URL}${endpoint}`);
      setProfile(response.data);
      setDisplayName(response.data.display_name || '');
      setAbout(response.data.about || '');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You can only view profiles of users you\'ve worked with');
        navigate(-1);
      } else {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/profile/me`, {
        display_name: displayName,
        about: about
      });
      toast.success('Profile updated');
      setEditing(false);
      fetchProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const formatJoinDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <DashboardShell>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
        </div>
      </DashboardShell>
    );
  }

  if (!profile) {
    return (
      <DashboardShell>
        <div className="text-center py-16">
          <p className="text-zinc-500">Profile not found</p>
        </div>
      </DashboardShell>
    );
  }

  const isContractor = profile.role === 'contractor';

  return (
    <DashboardShell>
      <div className="max-w-3xl mx-auto">
        {/* Profile Header Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-zinc-200 overflow-hidden mb-6"
        >
          {/* Cover / Header Area */}
          <div className="h-24 bg-gradient-to-r from-zinc-900 to-zinc-800" />
          
          {/* Profile Content */}
          <div className="px-6 pb-6 -mt-12">
            {/* Avatar */}
            <div className="relative inline-block mb-4">
              <div className="w-24 h-24 bg-zinc-200 rounded-full border-4 border-white flex items-center justify-center">
                {profile.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={profile.display_name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <User className="w-10 h-10 text-zinc-400" />
                )}
              </div>
              {isOwnProfile && !editing && (
                <button
                  className="absolute bottom-0 right-0 w-8 h-8 bg-zinc-900 rounded-full flex items-center justify-center text-white hover:bg-zinc-700 transition-colors"
                  data-testid="change-avatar-btn"
                >
                  <Camera className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Name & Edit Toggle */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {editing ? (
                  <Input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="text-2xl font-bold mb-1 h-auto py-1"
                    placeholder="Your name"
                    data-testid="edit-display-name"
                  />
                ) : (
                  <h1 className="text-2xl font-bold text-zinc-900">{profile.display_name}</h1>
                )}
                
                <div className="flex items-center gap-3 text-sm text-zinc-500 mt-1">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Joined {formatJoinDate(profile.joined_at)}
                  </span>
                  {isContractor && profile.years_experience > 0 && (
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-4 h-4" />
                      {profile.years_experience} years exp
                    </span>
                  )}
                </div>
              </div>
              
              {isOwnProfile && (
                <div className="flex gap-2">
                  {editing ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditing(false)}
                        disabled={saving}
                        data-testid="cancel-edit-btn"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={saving}
                        className="bg-zinc-900 hover:bg-zinc-800"
                        data-testid="save-profile-btn"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 mr-1" />
                        )}
                        Save
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing(true)}
                      data-testid="edit-profile-btn"
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Rating Summary */}
            <div className="flex items-center gap-4 mt-4 py-4 border-t border-zinc-100">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < Math.round(profile.average_rating || 0)
                          ? 'text-zinc-900 fill-zinc-900'
                          : 'text-zinc-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-lg font-bold text-zinc-900">
                  {(profile.average_rating || 0).toFixed(1)}
                </span>
              </div>
              <span className="text-zinc-500 text-sm">
                {profile.rating_count || 0} reviews
              </span>
              <span className="text-zinc-300">•</span>
              <span className="text-zinc-500 text-sm">
                {profile.completed_jobs_count || 0} jobs completed
              </span>
            </div>

            {/* About Section */}
            <div className="mt-4">
              <h2 className="text-sm font-semibold text-zinc-900 mb-2">About</h2>
              {editing ? (
                <Textarea
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                  placeholder="Tell others about yourself..."
                  className="min-h-[100px]"
                  data-testid="edit-about"
                />
              ) : (
                <p className="text-zinc-600 text-sm leading-relaxed">
                  {profile.about || (isOwnProfile ? 'Add a bio to tell others about yourself.' : 'No bio yet.')}
                </p>
              )}
            </div>

            {/* Contractor-specific: Trades & Certifications */}
            {isContractor && (
              <div className="mt-6 space-y-4">
                {/* Trades */}
                {profile.trades && profile.trades.length > 0 && (
                  <div>
                    <h2 className="text-sm font-semibold text-zinc-900 mb-2">Trades</h2>
                    <div className="flex flex-wrap gap-2">
                      {profile.trades.map((trade, i) => (
                        <span
                          key={i}
                          className="px-3 py-1.5 bg-zinc-100 text-zinc-700 rounded-full text-sm font-medium"
                        >
                          {trade}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trust Badges */}
                <div>
                  <h2 className="text-sm font-semibold text-zinc-900 mb-2">Trust & Safety</h2>
                  <div className="flex flex-wrap gap-2">
                    <TrustBadge
                      icon={Shield}
                      label="ID Verified"
                      verified={profile.is_identity_verified}
                    />
                    <TrustBadge
                      icon={Award}
                      label="Verified Contractor"
                      verified={profile.is_verified}
                    />
                    {profile.verified_certifications?.map((cert, i) => (
                      <TrustBadge
                        key={i}
                        icon={Award}
                        label={cert}
                        verified={true}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Reviews Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-zinc-200 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-zinc-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-zinc-900">
                Reviews ({profile.rating_count || 0})
              </h2>
              {isOwnProfile && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/reviews')}
                  className="text-zinc-600"
                >
                  View all
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
          
          <div className="px-6">
            {profile.reviews && profile.reviews.length > 0 ? (
              profile.reviews.slice(0, 5).map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))
            ) : (
              <div className="py-8 text-center">
                <Star className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No reviews yet</p>
              </div>
            )}
          </div>
          
          {profile.reviews && profile.reviews.length > 5 && (
            <div className="px-6 py-4 border-t border-zinc-100">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate(`/reviews/user/${profile.user_id}`)}
              >
                Show all {profile.rating_count} reviews
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </DashboardShell>
  );
};

export default ProfilePage;
